import { Button, Drawer, Empty } from 'antd';
import {
    chipBackground,
    isRowDiff,
    readableTagColor,
    tagShortName,
    type DiffEntry,
    type DiffKind,
    type TagOption,
} from './types';
import useIsDark from './useIsDark';
import { brandButton } from './brand';

interface Props {
    open: boolean;
    entries: DiffEntry[];
    tagsById: Record<number, TagOption>;
    scopeLabel: string;
    saving: boolean;
    onClose: () => void;
    onSave: () => void;
}

const GROUPS: { kind: DiffKind; label: string; sign: string; className: string }[] = [
    { kind: 'add-row', label: 'Thêm trạng thái nguồn', sign: '+', className: 'text-emerald-600' },
    { kind: 'remove-row', label: 'Xóa trạng thái nguồn', sign: '−', className: 'text-red-500' },
    { kind: 'add', label: 'Thêm mới', sign: '+', className: 'text-emerald-600' },
    { kind: 'remove', label: 'Xóa bỏ', sign: '−', className: 'text-red-500' },
    { kind: 'disable', label: 'Tắt', sign: '⏸', className: 'text-orange-500' },
    { kind: 'enable', label: 'Bật lại', sign: '▸', className: 'text-[#3e91ab]' },
];

function TagBadge({ tag, id, isDark }: { tag?: TagOption; id: number; isDark: boolean }) {
    return (
        <span
            className="font-mono text-[11px] px-1.5 py-0.5 rounded shrink-0"
            style={{
                backgroundColor: chipBackground(tag?.color, isDark),
                color: readableTagColor(tag?.color, isDark),
            }}
        >
            {tag?.code || tag?.name || `#${id}`}
        </span>
    );
}

/** Danh sách thay đổi chưa lưu, đọc được trước khi bấm Lưu. */
export default function DiffDrawer({
    open,
    entries,
    tagsById,
    scopeLabel,
    saving,
    onClose,
    onSave,
}: Props) {
    const isDark = useIsDark();

    return (
        <Drawer
            open={open}
            onClose={onClose}
            width={440}
            title={`${entries.length} thay đổi chưa lưu`}
            footer={
                <div className="flex items-center gap-2">
                    <Button onClick={onClose}>Đóng</Button>
                    <span className="flex-1" />
                    <Button
                        type="primary"
                        loading={saving}
                        onClick={onSave}
                        disabled={!entries.length}
                        className={brandButton(entries.length > 0)}
                    >
                        Lưu {entries.length} thay đổi
                    </Button>
                </div>
            }
        >
            {!entries.length && (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có thay đổi nào" />
            )}

            {GROUPS.map((g) => {
                const rows = entries.filter((e) => e.kind === g.kind);
                if (!rows.length) return null;
                return (
                    <div key={g.kind} className="mb-5">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-[#8a8a8a] mb-2">
                            {g.label} · {rows.length}
                        </div>
                        {rows.map((e) => (
                            <div
                                key={`${g.kind}-${e.fromTagId}-${e.toTagId}`}
                                className="flex items-center gap-2 px-2.5 py-2 mb-1 rounded-md bg-gray-50 dark:bg-[#1f1f1f] flex-wrap"
                            >
                                <span className={`font-mono w-3.5 text-center shrink-0 ${g.className}`}>
                                    {g.sign}
                                </span>
                                <TagBadge tag={tagsById[e.fromTagId]} id={e.fromTagId} isDark={isDark} />
                                {isRowDiff(e) ? (
                                    <span className="text-xs text-gray-500 dark:text-[#a0a0a0] truncate">
                                        {tagShortName(tagsById[e.fromTagId], e.fromTagId)}
                                        <span className="text-gray-400 dark:text-[#6b6b6b]">
                                            {' '}
                                            — chưa có đích nào
                                        </span>
                                    </span>
                                ) : (
                                    <>
                                        <span className="text-gray-300 dark:text-[#5a5a5a]">→</span>
                                        <TagBadge tag={tagsById[e.toTagId]} id={e.toTagId} isDark={isDark} />
                                        <span className="text-xs text-gray-500 dark:text-[#a0a0a0] truncate">
                                            {tagShortName(tagsById[e.toTagId], e.toTagId)}
                                        </span>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                );
            })}

            {!!entries.length && (
                <p className="text-xs text-gray-400 dark:text-[#8a8a8a] mt-4">
                    Áp dụng cho{' '}
                    <span className="font-medium text-gray-600 dark:text-gray-200">{scopeLabel}</span>.
                </p>
            )}
        </Drawer>
    );
}
